import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Disclaimer, B9Disclaimer } from "@/components/Disclaimers";
import HealthDataConsent, { useHealthConsent } from "@/components/HealthDataConsent";
import {
  Stethoscope, BedDouble, Plane, Receipt, Star, ShieldCheck, Check, Accessibility,
  MapPin, Loader2, ArrowRight, ArrowLeft, Users, Activity, ShieldPlus, Sparkles, Crown,
} from "lucide-react";
import { toast } from "sonner";

const SPECIALTIES = [
  "Cardiology & Cardiac Surgery", "Orthopedics", "Neurosurgery", "Transplant Surgery",
  "Bariatric & Metabolic Surgery", "Cosmetic & Plastic Surgery", "Vascular Surgery",
  "General & Laparoscopic Surgery", "ENT Surgery", "Urology",
  "Comprehensive Health Checkups", "Oncology & Cancer Care", "Gastroenterology",
  "Pulmonology & Respiratory", "Diagnostic Imaging & Lab Work",
  "Cardiac Rehabilitation", "Orthopedic & Sports Rehabilitation", "Neurological Rehabilitation",
  "Addiction Recovery & De-addiction", "Pain Management & Chronic Care",
  "Ayurveda & Traditional Medicine", "Yoga & Meditation Therapy", "Wellness & Preventive Retreats",
  "Stem Cell & Regenerative Therapy", "B9Concept — Trauma-Based Healing", "Dental & Maxillofacial", "Ophthalmology & Eye Care",
  "Dermatology & Hair Restoration", "Endocrinology & Diabetes",
  "Fertility & Reproductive Medicine", "Gynecology & Obstetrics",
];

const STEPS = [
  { icon: Stethoscope, label: "Provider" },
  { icon: BedDouble, label: "Stay" },
  { icon: Plane, label: "Flight" },
  { icon: ShieldPlus, label: "Extras" },
  { icon: Receipt, label: "Pay" },
];

const CONCIERGE = {
  essentials: { label: "Essentials", price: 299, perks: ["Airport pickup & drop", "Dedicated coordinator", "Appointment scheduling"] },
  premium: { label: "Premium", price: 499, perks: ["Everything in Essentials", "24/7 personal concierge", "Local SIM + translator", "Sightseeing & recovery planning"] },
};

const INSURANCE_PRICE = 89;
const money = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export default function Booking() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);

  const [specialty, setSpecialty] = useState(params.get("specialty") || "Cardiology");
  const providerParam = params.get("provider");
  const pkgParam = params.get("pkg");
  const [providers, setProviders] = useState([]);
  const [loadingMatch, setLoadingMatch] = useState(false);
  const [provider, setProvider] = useState(null);
  const [pkg, setPkg] = useState(null);

  const [filters, setFilters] = useState({ wheelchair_accessible: false, ground_floor: false, nurse_on_call: false, caregiver_room: false });
  const [accommodations, setAccommodations] = useState([]);
  const [accommodation, setAccommodation] = useState(null);
  const [nights, setNights] = useState(10);

  const [flights, setFlights] = useState([]);
  const [flight, setFlight] = useState(null);
  const [origin, setOrigin] = useState("New York (JFK)");
  const [insurance, setInsurance] = useState(false);
  const [conciergeTier, setConciergeTier] = useState(null);

  const [paying, setPaying] = useState(false);
  const { granted: healthGranted, refresh: refreshConsent } = useHealthConsent();
  const [showConsent, setShowConsent] = useState(false);

  const runMatch = async (spec) => {
    setLoadingMatch(true);
    setProvider(null); setPkg(null);
    try {
      const { data } = await api.post("/v1/match", { specialty: spec });
      setProviders(data.results);
      if (data.results.length === 0) toast.info("No accredited providers for this specialty yet");
    } catch { toast.error("Match failed"); } finally { setLoadingMatch(false); }
  };

  useEffect(() => { runMatch(specialty); /* eslint-disable-next-line */ }, []);

  // Deep-link preselect: /book?provider=<id>&specialty=<s>&pkg=<pkgId>
  useEffect(() => {
    if (!providerParam || provider || !providers.length) return;
    const p = providers.find((x) => x.id === providerParam);
    if (!p) return;
    setProvider(p);
    const pk = (pkgParam && p.packages.find((k) => k.id === pkgParam))
      || p.packages.find((k) => k.specialty === specialty) || p.packages[0];
    if (pk) setPkg(pk);
    // eslint-disable-next-line
  }, [providers, providerParam, pkgParam]);

  const loadAccommodations = async () => {
    const q = new URLSearchParams();
    if (provider) q.set("city", provider.city);
    Object.entries(filters).forEach(([k, v]) => v && q.set(k, "true"));
    const { data } = await api.get(`/v1/accommodations?${q.toString()}`);
    setAccommodations(data);
  };

  const loadFlights = async () => {
    const { data } = await api.get(`/v1/travel/search?origin=${encodeURIComponent(origin)}&destination_city=${encodeURIComponent(provider.city)}`);
    setFlights(data.offers);
  };

  useEffect(() => { if (step === 1 && provider) loadAccommodations(); /* eslint-disable-next-line */ }, [step, filters]);
  useEffect(() => { if (step === 2 && provider) loadFlights(); /* eslint-disable-next-line */ }, [step]);

  const medical = pkg?.price || 0;
  const accomTotal = (accommodation?.price_per_night || 0) * nights;
  const flightPrice = flight?.price || 0;
  const insurancePrice = insurance ? INSURANCE_PRICE : 0;
  const conciergePrice = conciergeTier ? CONCIERGE[conciergeTier].price : 0;
  const platformFee = medical * 0.15 + accomTotal * 0.12 + flightPrice * 0.03 + insurancePrice * 0.15 + conciergePrice;
  const tcv = medical + accomTotal + flightPrice + insurancePrice + conciergePrice;

  // Next available for steps 0-3; step 4 is the Pay step.
  const canNext = [!!pkg, !!accommodation, !!flight, true][step];

  const pay = async () => {
    if (!healthGranted) { setShowConsent(true); return; }
    await startCheckout();
  };

  const startCheckout = async () => {
    setPaying(true);
    try {
      const { data: itin } = await api.post("/v1/orchestration", {
        provider_id: provider.id, package_id: pkg.id, accommodation_id: accommodation.id,
        nights, flight_offer: flight, start_date: new Date().toISOString().slice(0, 10),
        post_op_constraints: Object.entries(filters).filter(([, v]) => v).map(([k]) => k),
        insurance, concierge_tier: conciergeTier,
      });
      const { data: checkout } = await api.post("/payments/checkout", {
        itinerary_id: itin.id, origin_url: window.location.origin,
      });
      window.location.href = checkout.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout");
      setPaying(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
      <h1 className="font-heading text-2xl font-bold text-[#101828]">Build your treatment bundle</h1>
      <p className="text-sm text-muted-foreground mt-1">One itinerary · one zero-fee invoice · escrow-protected.</p>

      {/* Stepper */}
      <div className="mt-6 flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s.label} className="flex items-center gap-2 flex-1 last:flex-none">
            <div className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ${
              i === step ? "bg-[#0D5C75] text-white" : i < step ? "bg-[#12B76A] text-white" : "bg-white border border-[#E4E7EC] text-muted-foreground"
            }`} data-testid={`step-${i}`}>
              {i < step ? <Check size={16} /> : <s.icon size={16} />} <span className="hidden sm:inline">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 rounded ${i < step ? "bg-[#12B76A]" : "bg-[#E4E7EC]"}`} />}
          </div>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {/* STEP 0: Provider */}
          {step === 0 && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                <div className="flex-1">
                  <Label>Specialty</Label>
                  <Select value={specialty} onValueChange={(v) => { setSpecialty(v); runMatch(v); }}>
                    <SelectTrigger className="mt-1.5" data-testid="specialty-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SPECIALTIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {(specialty.startsWith("B9Concept") || pkg?.specialty?.startsWith("B9Concept") || provider?.is_b9) && (
                <B9Disclaimer testid="b9-booking-disclaimer" />
              )}
              {loadingMatch ? (
                <div className="flex items-center gap-2 text-muted-foreground py-8"><Loader2 className="animate-spin" size={18} /> Matching accredited providers…</div>
              ) : providers.map((p) => (
                <div key={p.id} className={`rounded-xl border bg-white p-6 transition-transform duration-200 hover:-translate-y-1 ${provider?.id === p.id ? "border-[#0D5C75] ring-1 ring-[#0D5C75]" : "border-[#E4E7EC]"}`} data-testid={`provider-card-${p.id}`}>
                  <div className="flex gap-4">
                    <img src={p.image} alt={p.name} className="h-20 w-20 rounded-lg object-cover" />
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-heading font-bold text-lg text-[#101828]">{p.name}</h3>
                            {p.tier === "premium" && <Badge className="bg-[#F79009] text-white border-0"><Crown size={11} className="mr-1" />Premium</Badge>}
                            {p.tier === "enterprise" && <Badge className="bg-[#0D5C75] text-white border-0"><Crown size={11} className="mr-1" />Enterprise Partner</Badge>}
                            {p.featured && <Badge className="bg-[#EA580C] text-white border-0">Featured</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin size={13} /> {p.city}</p>
                          {p.packages?.length > 0 && (
                            <div className="text-xs text-muted-foreground mt-1">Starting from{" "}
                              <span className="font-semibold text-[#0D5C75]">${(p.starting_price ?? Math.min(...p.packages.map((pk) => pk.price))).toLocaleString()}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <Badge className="bg-[#12B76A] text-white border-0">Match {p.match_score}</Badge>
                          <div className="relative h-12 w-12" title={`${p.success_rate}% success rate`}>
                            <svg viewBox="0 0 36 36" className="h-12 w-12 -rotate-90">
                              <circle cx="18" cy="18" r="16" fill="none" stroke="#E4E7EC" strokeWidth="3" />
                              <circle cx="18" cy="18" r="16" fill="none" stroke={p.success_rate >= 90 ? "#10B981" : "#D97706"}
                                strokeWidth="3" strokeLinecap="round" strokeDasharray={`${(p.success_rate / 100) * 100.5} 100.5`} />
                            </svg>
                            <span className="absolute inset-0 grid place-items-center text-[10px] font-bold text-[#101828]">{p.success_rate}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {p.accreditation.map((a) => <Badge key={a} variant="outline" className="border-[#0D5C75]/30 text-[#0D5C75]"><ShieldCheck size={12} className="mr-1" />{a}</Badge>)}
                        <Badge variant="outline" className="border-[#E4E7EC] text-muted-foreground"><Activity size={12} className="mr-1" />{p.success_rate}% success</Badge>
                        <Badge variant="outline" className="border-[#E4E7EC] text-muted-foreground"><Users size={12} className="mr-1" />{p.capacity} slots</Badge>
                        <Badge variant="outline" className="border-[#E4E7EC] text-muted-foreground"><Star size={12} className="mr-1 text-[#F79009]" />{p.rating}</Badge>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 grid sm:grid-cols-2 gap-3">
                    {(() => {
                      const matching = p.packages.filter((pk) => pk.specialty === specialty);
                      return (matching.length ? matching : p.packages);
                    })().map((pk) => (
                      <button key={pk.id} onClick={() => { setProvider(p); setPkg(pk); }}
                        className={`text-left rounded-lg border p-3 transition-colors duration-200 ${provider?.id === p.id && pkg?.id === pk.id ? "border-[#2A9D8F] bg-[#E9F5F9]" : "border-[#E4E7EC] hover:bg-[#F7F9FC]"}`}
                        data-testid={`package-${pk.id}`}>
                        <div className="font-medium text-sm text-[#101828]">{pk.name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{pk.duration_days} days</div>
                        <div className="font-heading font-bold text-[#0D5C75] mt-1">{money(pk.price)}</div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* STEP 1: Accommodation */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="rounded-xl border border-[#E4E7EC] bg-white p-4">
                <div className="flex items-center gap-2 text-sm font-medium text-[#101828] mb-3"><Accessibility size={16} className="text-[#0D5C75]" /> Post-op accessibility filters</div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ["wheelchair_accessible", "Wheelchair accessible"],
                    ["ground_floor", "Ground floor"],
                    ["nurse_on_call", "Nurse on-call"],
                    ["caregiver_room", "Caregiver room"],
                  ].map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`filter-${k}`}>
                      <Checkbox checked={filters[k]} onCheckedChange={(v) => setFilters({ ...filters, [k]: !!v })} />
                      {label}
                    </label>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <Label className="text-sm">Nights</Label>
                  <Input type="number" min={1} max={90} value={nights} onChange={(e) => setNights(Math.max(1, Number(e.target.value)))} className="w-24" data-testid="nights-input" />
                </div>
              </div>
              {accommodations.map((a) => (
                <div key={a.id} onClick={() => setAccommodation(a)}
                  className={`cursor-pointer rounded-xl border bg-white p-4 flex gap-4 transition-transform duration-200 hover:-translate-y-1 ${accommodation?.id === a.id ? "border-[#0D5C75] ring-1 ring-[#0D5C75]" : "border-[#E4E7EC]"}`}
                  data-testid={`accommodation-card-${a.id}`}>
                  <img src={a.image} alt={a.name} className="h-24 w-24 rounded-lg object-cover" />
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <h3 className="font-heading font-bold text-[#101828]">{a.name}</h3>
                      <div className="font-heading font-bold text-[#0D5C75]">{money(a.price_per_night)}<span className="text-xs text-muted-foreground font-normal">/night</span></div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{a.about}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {a.post_op_flags.wheelchair_accessible && <Badge variant="outline" className="text-xs border-[#E4E7EC]">Wheelchair</Badge>}
                      {a.post_op_flags.ground_floor && <Badge variant="outline" className="text-xs border-[#E4E7EC]">Ground floor</Badge>}
                      {a.post_op_flags.nurse_on_call && <Badge variant="outline" className="text-xs border-[#E4E7EC]">Nurse on-call</Badge>}
                      <Badge variant="outline" className="text-xs border-[#E4E7EC]">{a.post_op_flags.clinic_proximity_km}km to clinic</Badge>
                    </div>
                  </div>
                </div>
              ))}
              {accommodations.length === 0 && <p className="text-muted-foreground text-sm py-6">No stays match these filters. Try relaxing them.</p>}
            </div>
          )}

          {/* STEP 2: Flight */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="rounded-xl border border-[#E4E7EC] bg-white p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
                <div className="flex-1">
                  <Label>Departing from</Label>
                  <Input value={origin} onChange={(e) => setOrigin(e.target.value)} className="mt-1.5" data-testid="origin-input" />
                </div>
                <Button variant="outline" className="border-[#0D5C75]/30 text-[#0D5C75] hover:bg-[#E9F5F9]" onClick={loadFlights} data-testid="search-flights-btn">Search</Button>
              </div>
              {flights.map((f) => (
                <div key={f.id} onClick={() => setFlight(f)}
                  className={`cursor-pointer rounded-xl border bg-white p-4 flex items-center justify-between transition-transform duration-200 hover:-translate-y-1 ${flight?.id === f.id ? "border-[#0D5C75] ring-1 ring-[#0D5C75]" : "border-[#E4E7EC]"}`}
                  data-testid={`flight-card-${f.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="grid place-items-center h-11 w-11 rounded-lg bg-[#E9F5F9] text-[#0D5C75]"><Plane size={20} /></span>
                    <div>
                      <div className="font-medium text-[#101828]">{f.airline} · {f.cabin}</div>
                      <div className="text-sm text-muted-foreground">{f.origin} → {f.destination} · {f.duration} · {f.stops === 0 ? "Non-stop" : `${f.stops} stop`}</div>
                    </div>
                  </div>
                  <div className="font-heading font-bold text-[#0D5C75] text-lg">{money(f.price)}</div>
                </div>
              ))}
              <Disclaimer testid="flight-disclaimer">
                Flights are booked through third-party travel partners. SarvaSeva earns a small affiliate commission and is not liable for airline schedule changes, cancellations, or baggage policies.
              </Disclaimer>
            </div>
          )}

          {/* STEP 3: Extras (Insurance + Concierge) */}
          {step === 3 && (
            <div className="space-y-4">
              {/* Insurance */}
              <div className="rounded-xl border border-[#E4E7EC] bg-white p-5" data-testid="insurance-section">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <span className="grid place-items-center h-10 w-10 rounded-lg bg-[#E9F5F9] text-[#0D5C75]"><ShieldPlus size={20} /></span>
                    <div>
                      <div className="font-heading font-bold text-[#101828]">Travel Medical Insurance</div>
                      <p className="text-sm text-muted-foreground mt-0.5 max-w-md">Optional coverage for trip cancellation, medical evacuation and emergency care during your stay abroad.</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="font-heading font-bold text-[#0D5C75]">{money(INSURANCE_PRICE)}</div>
                    <Switch checked={insurance} onCheckedChange={setInsurance} data-testid="insurance-toggle" />
                  </div>
                </div>
                <Disclaimer testid="insurance-disclaimer" className="mt-3">
                  Travel insurance is provided by an independent third-party insurer. Coverage terms, exclusions and claims are governed solely by the insurer's policy. SarvaSeva is not the insurer and does not provide medical or financial advice.
                </Disclaimer>
              </div>

              {/* Concierge */}
              <div className="rounded-xl border border-[#E4E7EC] bg-white p-5">
                <div className="flex items-center gap-2 mb-1"><Sparkles size={18} className="text-[#0D5C75]" /><div className="font-heading font-bold text-[#101828]">Premium Concierge (optional)</div></div>
                <p className="text-sm text-muted-foreground mb-4">White-glove support for your medical journey. 100% optional.</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  <button onClick={() => setConciergeTier(null)}
                    className={`text-left rounded-lg border p-4 transition-colors ${!conciergeTier ? "border-[#2A9D8F] bg-[#E9F5F9]" : "border-[#E4E7EC] hover:bg-[#F7F9FC]"}`}
                    data-testid="concierge-none">
                    <div className="font-medium text-sm text-[#101828]">No concierge</div>
                    <div className="font-heading font-bold text-[#0D5C75] mt-1">$0</div>
                  </button>
                  {Object.entries(CONCIERGE).map(([key, c]) => (
                    <button key={key} onClick={() => setConciergeTier(key)}
                      className={`text-left rounded-lg border p-4 transition-colors ${conciergeTier === key ? "border-[#2A9D8F] bg-[#E9F5F9]" : "border-[#E4E7EC] hover:bg-[#F7F9FC]"}`}
                      data-testid={`concierge-${key}`}>
                      <div className="font-medium text-sm text-[#101828]">{c.label}</div>
                      <div className="font-heading font-bold text-[#0D5C75] mt-1">{money(c.price)}</div>
                      <ul className="mt-2 space-y-1">
                        {c.perks.map((p) => <li key={p} className="text-xs text-muted-foreground flex items-start gap-1"><Check size={11} className="mt-0.5 text-[#12B76A] shrink-0" />{p}</li>)}
                      </ul>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: Review & Pay */}
          {step === 4 && (
            <div className="space-y-3">
              {[
                { icon: Stethoscope, label: provider?.name, sub: pkg?.name, price: medical },
                { icon: BedDouble, label: accommodation?.name, sub: `${nights} nights`, price: accomTotal },
                { icon: Plane, label: `${flight?.airline}`, sub: `${flight?.origin} → ${flight?.destination}`, price: flightPrice },
                ...(insurance ? [{ icon: ShieldPlus, label: "Travel Medical Insurance", sub: "Third-party coverage", price: insurancePrice }] : []),
                ...(conciergeTier ? [{ icon: Sparkles, label: `Concierge · ${CONCIERGE[conciergeTier].label}`, sub: "White-glove support", price: conciergePrice }] : []),
              ].map((row, i) => (
                <div key={i} className="rounded-xl border border-[#E4E7EC] bg-white p-4 flex items-center justify-between" data-testid={`review-row-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="grid place-items-center h-10 w-10 rounded-lg bg-[#E9F5F9] text-[#0D5C75]"><row.icon size={18} /></span>
                    <div><div className="font-medium text-[#101828]">{row.label}</div><div className="text-sm text-muted-foreground">{row.sub}</div></div>
                  </div>
                  <div className="font-heading font-bold text-[#101828]">{money(row.price)}</div>
                </div>
              ))}
              <div className="rounded-xl bg-[#0D5C75] text-white p-5">
                <div className="flex items-center gap-2 text-sm text-white/80 mb-3"><ShieldCheck size={16} /> Provider-funded escrow · zero fees to you</div>
                <div className="space-y-1.5 text-sm text-white/85">
                  <div className="flex justify-between"><span>Platform take-rate (medical/stay/travel/add-ons)</span><span>{money(platformFee)}</span></div>
                  <div className="flex justify-between"><span>Provider payouts (released on milestones)</span><span>{money(tcv - platformFee)}</span></div>
                </div>
                <div className="border-t border-white/20 mt-3 pt-3 flex justify-between items-center">
                  <span className="text-white/80">You pay (Total Contract Value)</span>
                  <span className="font-heading text-2xl font-extrabold" data-testid="tcv-amount">{money(tcv)}</span>
                </div>
              </div>
              <Disclaimer testid="payment-disclaimer">
                Payments are processed securely via Stripe and held in a milestone-based escrow. Funds are released to providers only as your treatment milestones are reached. SarvaSeva does not guarantee medical outcomes; all clinical decisions rest with you and your chosen provider.
              </Disclaimer>
            </div>
          )}
        </div>

        {/* Summary sidebar */}
        <div className="lg:col-span-1">
          <div className="rounded-xl border border-[#E4E7EC] bg-white p-5 sticky top-20">
            <h3 className="font-heading font-bold text-[#101828]">Your bundle</h3>
            <div className="mt-4 space-y-3 text-sm">
              <Row label="Treatment" value={pkg ? money(medical) : "—"} name={provider?.name} />
              <Row label="Accommodation" value={accommodation ? money(accomTotal) : "—"} name={accommodation?.name} />
              <Row label="Flight" value={flight ? money(flightPrice) : "—"} name={flight?.airline} />
              {insurance && <Row label="Insurance" value={money(insurancePrice)} name="Travel medical" />}
              {conciergeTier && <Row label="Concierge" value={money(conciergePrice)} name={CONCIERGE[conciergeTier].label} />}
            </div>
            <div className="border-t border-[#E4E7EC] mt-4 pt-4 flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Total</span>
              <span className="font-heading text-xl font-extrabold text-[#0D5C75]" data-testid="sidebar-total">{money(tcv)}</span>
            </div>

            <div className="mt-5 flex gap-2">
              {step > 0 && (
                <Button variant="outline" className="flex-1 border-[#E4E7EC]" onClick={() => setStep(step - 1)} data-testid="back-btn">
                  <ArrowLeft size={16} className="mr-1" /> Back
                </Button>
              )}
              {step < 4 ? (
                <Button className="flex-1 rounded-full bg-[#0D5C75] text-white hover:bg-[#094A5E] hover:text-white disabled:opacity-50"
                  disabled={!canNext} onClick={() => setStep(step + 1)} data-testid="next-btn">
                  Next <ArrowRight size={16} className="ml-1" />
                </Button>
              ) : (
                <Button className="flex-1 rounded-full bg-[#12B76A] text-white hover:bg-[#0e9a59] hover:text-white"
                  disabled={paying} onClick={pay} data-testid="pay-btn">
                  {paying ? <Loader2 className="animate-spin mr-2" size={16} /> : <Receipt size={16} className="mr-1.5" />}
                  Pay {money(tcv)}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      <HealthDataConsent
        open={showConsent}
        onOpenChange={setShowConsent}
        onGranted={() => { refreshConsent(); startCheckout(); }}
      />
    </div>
  );
}

function Row({ label, value, name }) {
  return (
    <div className="flex justify-between items-start">
      <div><div className="text-muted-foreground">{label}</div>{name && <div className="text-xs text-[#101828]">{name}</div>}</div>
      <div className="font-medium text-[#101828]">{value}</div>
    </div>
  );
}
